import { useRef, useEffect, useCallback, useState } from "react";
import { Loader2, ChevronDown } from "lucide-react";
import StreamingMessage from "../messages/StreamingMessage";

const ThinkingStatus = ({ text }) => (
  <div className="flex items-center gap-2.5">
    <Loader2 className="h-3.5 w-3.5 animate-spin text-mutedText shrink-0" />
    <span className="text-[0.8125rem] font-normal text-mutedText animate-pulse">
      {text || "Thinking..."}
    </span>
  </div>
);

const NEAR_BOTTOM_PX = 150;

const ChatThread = ({
  displayThisThread,
  streamingText,
  showLoading,
  showStreamingOutput,
  streamingCitations,
  statusText,
  messageCount,
  isTeamChat = false,
}) => {
  const scrollRef = useRef(null);
  const isNearBottomRef = useRef(true);
  const prevCountRef = useRef(messageCount);
  const [newMsgCount, setNewMsgCount] = useState(0);

  const checkNearBottom = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return true;
    return el.scrollHeight - el.scrollTop - el.clientHeight < NEAR_BOTTOM_PX;
  }, []);

  const scrollToBottom = useCallback((behavior = "instant") => {
    requestAnimationFrame(() => {
      const el = scrollRef.current;
      if (el) el.scrollTo({ top: el.scrollHeight, behavior });
    });
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const handleScroll = () => {
      const nearBottom = checkNearBottom();
      isNearBottomRef.current = nearBottom;
      if (nearBottom && newMsgCount > 0) {
        setNewMsgCount(0);
      }
    };

    el.addEventListener("scroll", handleScroll, { passive: true });
    return () => el.removeEventListener("scroll", handleScroll);
  }, [checkNearBottom, newMsgCount]);

  useEffect(() => {
    if (!isTeamChat) {
      scrollToBottom();
      return;
    }

    const countDiff = messageCount - prevCountRef.current;
    prevCountRef.current = messageCount;

    if (countDiff <= 0) return;

    if (isNearBottomRef.current) {
      scrollToBottom("smooth");
    } else {
      setNewMsgCount((prev) => prev + countDiff);
    }
  }, [messageCount, isTeamChat, scrollToBottom]);

  useEffect(() => {
    if (!isTeamChat) {
      scrollToBottom();
    }
  }, [streamingText, showLoading, statusText, isTeamChat, scrollToBottom]);

  const handleNewMsgClick = useCallback(() => {
    setNewMsgCount(0);
    scrollToBottom("smooth");
  }, [scrollToBottom]);

  const showThinking = showLoading || (!!statusText && !showStreamingOutput);

  return (
    <div className="h-full min-h-0 flex flex-col relative">
      <div
        ref={scrollRef}
        className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-6 pb-4"
      >
        <div className="flex flex-col justify-end min-h-full">
          <div className="w-full space-y-5 pt-16">
            {displayThisThread}
            {(showThinking || showStreamingOutput) && (
              <div className="flex gap-3 -mt-4">
                <div className="flex flex-col items-center flex-shrink-0">
                  <div className="w-8 flex justify-center flex-1">
                    <div className="w-px bg-surfaceBorder" />
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  {showThinking && (
                    <div className="py-2">
                      <ThinkingStatus text={statusText} />
                    </div>
                  )}
                  {showStreamingOutput && (
                    <div className="max-w-[720px]">
                      <div className="text-sm font-normal leading-relaxed text-mainText">
                        <StreamingMessage
                          text={streamingText}
                          citations={streamingCitations}
                        />
                        <span className="inline-block w-[3px] h-[18px] ml-0.5 bg-mainText rounded-full animate-pulse align-middle" />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {newMsgCount > 0 && (
        <button
          onClick={handleNewMsgClick}
          className="absolute bottom-4 left-1/2 -translate-x-1/2 z-20 flex items-center gap-x-1.5 px-3 py-1.5 rounded-full bg-velBlue hover:bg-velBlue/80 text-white text-xs font-normal shadow-login-panel transition-all cursor-pointer"
          style={{ animation: "toast-in 200ms ease-out" }}
        >
          <ChevronDown className="h-3.5 w-3.5" strokeWidth={2.5} />
          {newMsgCount} new message{newMsgCount !== 1 ? "s" : ""}
        </button>
      )}

      <style>{`
        @keyframes toast-in {
          from { opacity: 0; transform: translateX(-50%) translateY(10px); }
          to { opacity: 1; transform: translateX(-50%) translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default ChatThread;
